import React, { useState, useCallback } from "react";
import {
	Container,
	Grid,
	Typography,
	Box,
	Paper,
	CircularProgress,
	Button,
	Stack,
	Alert,
	IconButton,
} from "@mui/material";
import { format } from "date-fns";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";

const UploadPage = ({ onBack, onApplyFilters, dateRange }) => {
	const [uploadedFile, setUploadedFile] = useState(null);
	const [uploadedData, setUploadedData] = useState([]);
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [fileInputKey, setFileInputKey] = useState(0);
	const [processingFailed, setProcessingFailed] = useState(false);
	const resolvedDateRange = dateRange || { startDate: new Date(), endDate: new Date() };
	const [showFilterPreview, setShowFilterPreview] = useState(false);
	const [filterPreviewData, setFilterPreviewData] = useState(null);
	const [additionalFilterOptions, setAdditionalFilterOptions] = useState({});

	// Helper functions for PDF generation (same as main page)
	const formatNumber = (num) => {
		if (num === null || num === undefined || isNaN(num)) return "0";
		return Math.round(num).toLocaleString("en-IN");
	};

	// Filter labels (same as main page)
	const filterLabels = {
		brand: "Brand",
		subBrand: "Sub Brand",
		department: "Department",
		subCategory: "Sub Category",
		style: "Style",
		color: "Color",
		sleeve: "Sleeve",
		division: "Division",
		season: "Season",
		fashionability: "Fashionability",
		occasion: "Occasion",
		palette: "Palette",
		fitType: "Fit Type",
		channel: "Channel",
		store: "Store",
		city: "City",
		state: "State",
		region: "Region",
		tier: "Tier",
	};

	// Function to extract unique values from uploaded CSV and show filter preview
	const handleApplyToFilter = async () => {
		if (!uploadedData || uploadedData.length === 0) {
			setError("No CSV data available to apply as filters");
			return;
		}

		setIsUploading(true);
		setError("");
		setProcessingFailed(false); // Reset processing failed state

		try {
			// Extract keys from uploaded data
			const keys = uploadedData.map((row) =>
				[row.brand || "", row.department || "", row.style || "", row.color || "", row["pallete type"] || ""]
					.map((s) => (s || "").trim())
					.join("-")
			);

			// Fetch additional filter options based on the keys
			const filterOptionsResponse = await pythonVmInstance.post("/st_pictorial/filter-options/by-keys", {
				keys,
			});

			if (filterOptionsResponse.data && filterOptionsResponse.data.filter_options) {
				console.log("Additional filter options fetched:", filterOptionsResponse.data.filter_options);
				// Store the additional filter options
				setAdditionalFilterOptions(filterOptionsResponse.data.filter_options);
			}

			// Extract unique values from each column
			const uniqueValues = {};

			// Get all column names from the first row
			const columns = Object.keys(uploadedData[0] || {});

			columns.forEach((column) => {
				const values = [...new Set(uploadedData.map((row) => row[column]).filter((val) => val && val.trim()))];
				if (values.length > 0) {
					uniqueValues[column] = values;
				}
			});

			// Map CSV column names to filter names
			const columnToFilterMap = {
				brand: "brand",
				department: "department",
				style: "style",
				color: "color",
				"pallete type": "palette",
				palette: "palette",
				subBrand: "subBrand",
				subCategory: "subCategory",
				sleeve: "sleeve",
				division: "division",
				season: "season",
				fashionability: "fashionability",
				occasion: "occasion",
				fitType: "fitType",
				"fit type": "fitType",
				channel: "channel",
				store: "store",
				city: "city",
				state: "state",
				region: "region",
				tier: "tier",
			};

			// Create filters object
			const filters = {
				start_date: format(resolvedDateRange.startDate, "yyyy-MM-dd"),
				end_date: format(resolvedDateRange.endDate, "yyyy-MM-dd"),
			};

			// Add mapped filters from CSV data
			Object.entries(uniqueValues).forEach(([column, values]) => {
				const filterKey = columnToFilterMap[column];
				if (filterKey && values.length > 0) {
					filters[filterKey] = values.join(",");
				}
			});

			// Add additional filter options from database if available
			if (filterOptionsResponse.data && filterOptionsResponse.data.filter_options) {
				Object.entries(filterOptionsResponse.data.filter_options).forEach(([filterKey, values]) => {
					if (values && values.length > 0) {
						// Map database filter keys to API filter keys
						const apiFilterKey =
							filterKey === "subBrands"
								? "subBrand"
								: filterKey === "subCategories"
								? "subCategory"
								: filterKey === "sleeves"
								? "sleeve"
								: filterKey === "seasons"
								? "season"
								: filterKey === "divisions"
								? "division"
								: filterKey === "fashionability"
								? "fashionability"
								: filterKey === "occasions"
								? "occasion"
								: filterKey;

						// Only add if not already present from CSV data
						if (!filters[apiFilterKey]) {
							filters[apiFilterKey] = values.join(",");
						}
					}
				});
			}

			// Store the filter data and show preview
			setFilterPreviewData({ filters, dateRange: resolvedDateRange, uniqueValues });
			setShowFilterPreview(true);
			setSuccess("Filter preview generated with additional database options. Please review and confirm.");
			// Reset file input to allow uploading the same file again
			setFileInputKey((prevKey) => prevKey + 1);
		} catch (error) {
			console.error("Error generating filter preview:", error);
			setError("Failed to generate filter preview: " + error.message);
			setProcessingFailed(true); // Mark as failed
			setSuccess(""); // Clear success message
		} finally {
			setIsUploading(false);
		}
	};

	// Function to handle routing to main page after filter confirmation
	const handleRouteToMainPage = () => {
		if (!filterPreviewData) {
			setError("No filter data available");
			return;
		}

		try {
			// Call the callback to set filters and navigate to main page
			if (onApplyFilters) {
				onApplyFilters(filterPreviewData.filters, filterPreviewData.dateRange);
			}

			setSuccess("Filters set successfully! Redirecting to main page...");
		} catch (error) {
			console.error("Error setting filters:", error);
			setError("Failed to set filters: " + error.message);
		}
	};

	// Function to cancel filter preview
	const handleCancelFilterPreview = () => {
		setShowFilterPreview(false);
		setFilterPreviewData(null);
		setSuccess("");
		setProcessingFailed(false); // Reset processing failed state
		// Reset file input to allow uploading the same file again
		setFileInputKey((prevKey) => prevKey + 1);
	};

	// File upload handlers
	const handleFileUpload = (event) => {
		const file = event.target.files[0];
		if (file) {
			// Check if file is CSV
			if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
				setError("Please upload a CSV file only.");
				return;
			}

			setUploadedFile(file);
			parseCSVFile(file);
		}
	};

	const parseCSVFile = (file) => {
		setIsUploading(true);
		setError("");
		setSuccess("");

		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const csvContent = e.target.result;
				const lines = csvContent.split("\n");

				if (lines.length < 2) {
					setError("CSV file must contain at least a header row and one data row.");
					setIsUploading(false);
					return;
				}

				// Parse headers
				const headers = lines[0].split(",").map((header) => header.trim().replace(/"/g, ""));

				// Parse data rows
				const data = lines
					.slice(1)
					.filter((line) => line.trim())
					.map((line) => {
						const values = line.split(",").map((value) => value.trim().replace(/"/g, ""));
						const row = {};
						headers.forEach((header, index) => {
							row[header] = values[index] || "";
						});
						return row;
					});

				setUploadedData(data);
				console.log("Parsed CSV data:", data);

				// Don't automatically process the data - wait for submit button
				setSuccess('CSV file uploaded successfully! Click "Apply to Filter" to continue.');
			} catch (error) {
				console.error("Error parsing CSV:", error);
				setError("Error parsing CSV file. Please check the file format.");
			} finally {
				setIsUploading(false);
			}
		};

		reader.onerror = () => {
			setError("Error reading file.");
			setIsUploading(false);
		};

		reader.readAsText(file);
	};

	// Add missing functions
	const downloadSampleCSV = () => {
		try {
			// Sample CSV content with brand as the first column
			const sampleData = [
				"brand,department,style,color,pallete type",
				"RARE RABBIT,T-SHIRT,REV,BLACK,PRIMARY",
				"RARE RABBIT,SHIRT,CLASSIC,BEIGE,DARK",
			];
			const csvContent = sampleData.join("\n");
			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", `sample-csv-structure-${format(new Date(), "yyyy-MM-dd")}.csv`);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			setSuccess("Sample CSV file downloaded successfully!");
		} catch (error) {
			console.error("Error generating sample CSV:", error);
			setError(`Failed to generate sample CSV: ${error.message}`);
		}
	};

	const clearUploadedData = () => {
		setUploadedFile(null);
		setUploadedData([]);
		setError("");
		setSuccess("");
		setShowFilterPreview(false);
		setFilterPreviewData(null);
		setAdditionalFilterOptions({});
		setFileInputKey((prevKey) => prevKey + 1);
		setProcessingFailed(false);
	};

	return (
		<Container maxWidth="lg" sx={{ pt: 3 }}>
			<Paper sx={{ p: 4, backgroundColor: "#fff" }}>
				{/* Header */}
				<Box sx={{ mb: 4 }}>
					<Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }} variant="outlined">
						Back to Main Page
					</Button>
					<Typography variant="h4" sx={{ color: "#000", fontWeight: 600, mb: 1 }}>
						Sell Through Report Generator and Filter Uploader
					</Typography>
					<Typography variant="body1" sx={{ color: "#666" }}>
						Upload a CSV file to apply its data as filters on the main page. Date range is set on the main page.
					</Typography>
				</Box>

				{/* Error and Success Messages */}
				{error && (
					<Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
						{error}
					</Alert>
				)}
				{success && (
					<Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
						{success}
					</Alert>
				)}

				{/* Upload Section */}
				<Box sx={{ mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2, backgroundColor: "#fafafa" }}>
					<Typography variant="h6" sx={{ mb: 3, color: "#000", fontWeight: 600 }}>
						Upload CSV File
					</Typography>

					{/* File Upload */}
					<Box sx={{ mb: 3 }}>
						<Typography variant="subtitle1" sx={{ mb: 2, color: "#000", fontWeight: 500 }}>
							Upload CSV File
						</Typography>
						<Grid container spacing={3} alignItems="center" justifyContent="flex-start">
							<Grid item>
								<input
									accept=".csv"
									style={{ display: "none" }}
									id="csv-file-upload"
									type="file"
									onChange={handleFileUpload}
									disabled={isUploading}
									key={fileInputKey}
								/>
								<label htmlFor="csv-file-upload">
									<Button
										variant="outlined"
										component="span"
										disabled={isUploading}
										startIcon={<UploadFileIcon />}
										size="large"
										sx={{
											borderColor: "#000",
											color: "#000",
											"&:hover": {
												borderColor: "#333",
												backgroundColor: "rgba(0, 0, 0, 0.04)",
											},
										}}>
										{uploadedFile ? "Change File" : "Upload CSV File"}
									</Button>
								</label>
							</Grid>
							<Grid item>
								<Button
									variant="outlined"
									onClick={downloadSampleCSV}
									size="large"
									startIcon={<UploadFileIcon sx={{ transform: "rotate(180deg)" }} />}
									sx={{
										borderColor: "#007bff",
										color: "#007bff",
										"&:hover": {
											borderColor: "#0056b3",
											backgroundColor: "rgba(0, 123, 255, 0.04)",
										},
									}}>
									Download Sample CSV
								</Button>
							</Grid>
						</Grid>
					</Box>

					{/* Action Buttons */}
					<Box sx={{ mb: 3 }}>
						<Grid container spacing={2} alignItems="center" justifyContent="flex-start">
							<Grid item>
								{!showFilterPreview ? (
									<Button
										variant="contained"
										onClick={handleApplyToFilter}
										disabled={uploadedData.length === 0}
										size="large"
										sx={{
											backgroundColor: "#007bff",
											"&:hover": {
												backgroundColor: "#0056b3",
											},
											"&:disabled": {
												backgroundColor: "#6c757d",
											},
										}}>
										Apply to Filter
									</Button>
								) : (
									<Button
										variant="contained"
										onClick={handleRouteToMainPage}
										size="large"
										sx={{
											backgroundColor: "#28a745",
											"&:hover": {
												backgroundColor: "#218838",
											},
										}}>
										Set Filters & Go to Main Page
									</Button>
								)}
							</Grid>
							<Grid item>
								<Stack direction="row" spacing={2} justifyContent="flex-end">
									{showFilterPreview && (
										<Button
											variant="outlined"
											onClick={handleCancelFilterPreview}
											size="large"
											sx={{
												borderColor: "#6c757d",
												color: "#6c757d",
												"&:hover": {
													borderColor: "#545b62",
													backgroundColor: "rgba(108, 117, 125, 0.04)",
												},
											}}>
											Cancel
										</Button>
									)}
									{processingFailed && !showFilterPreview && (
										<Button
											variant="outlined"
											onClick={clearUploadedData}
											size="large"
											sx={{
												borderColor: "#dc3545",
												color: "#dc3545",
												"&:hover": {
													borderColor: "#c82333",
													backgroundColor: "rgba(220, 53, 69, 0.04)",
												},
											}}>
											Reset & Try Again
										</Button>
									)}
									{uploadedData.length > 0 && !showFilterPreview && !processingFailed && (
										<Button
											variant="outlined"
											onClick={clearUploadedData}
											size="large"
											sx={{
												borderColor: "#dc3545",
												color: "#dc3545",
												"&:hover": {
													borderColor: "#c82333",
													backgroundColor: "rgba(220, 53, 69, 0.04)",
												},
											}}>
											Clear Data
										</Button>
									)}
								</Stack>
							</Grid>
						</Grid>
					</Box>

					{/* Filter Preview */}
					{showFilterPreview && filterPreviewData && (
						<Box sx={{ mb: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1, backgroundColor: "#f8f9fa" }}>
							<Typography variant="subtitle2" sx={{ mb: 1, color: "#000", fontWeight: 600 }}>
								Filter Preview (will be set on main page):
							</Typography>
							<Grid container spacing={1}>
								{Object.entries(filterPreviewData.uniqueValues).map(([column, values]) => (
									<Grid item xs={12} sm={6} md={4} key={column}>
										<Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
											{column}: <span style={{ color: "#007bff" }}>{values.join(", ")}</span>
										</Typography>
									</Grid>
								))}
							</Grid>
							{/* Additional Filter Options from Database */}
							{Object.keys(additionalFilterOptions).length > 0 && (
								<Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}>
									<Typography variant="subtitle2" sx={{ mb: 1, color: "#000", fontWeight: 600 }}>
										Additional Filter Options (from database):
									</Typography>
									<Grid container spacing={1}>
										{Object.entries(additionalFilterOptions).map(([filterKey, values]) => (
											<Grid item xs={12} sm={6} md={4} key={filterKey}>
												<Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
													{filterLabels[filterKey] || filterKey}:{" "}
													<span style={{ color: "#28a745" }}>{values.join(", ")}</span>
												</Typography>
											</Grid>
										))}
									</Grid>
								</Box>
							)}
							<Typography variant="body2" sx={{ mt: 2, color: "#666", fontStyle: "italic" }}>
								Date Range: {format(resolvedDateRange.startDate, "dd-MM-yyyy")} - {format(resolvedDateRange.endDate, "dd-MM-yyyy")}
							</Typography>
							<Typography variant="body2" sx={{ mt: 1, color: "#666", fontStyle: "italic" }}>
								Note: Filters will be pre-selected on the main page. You can modify them before applying.
							</Typography>
						</Box>
					)}

					{/* Processing Status */}
					{isUploading && (
						<Box
							sx={{
								mt: 3,
								display: "flex",
								alignItems: "center",
								gap: 2,
								p: 3,
								backgroundColor: "#e3f2fd",
								borderRadius: 1,
							}}>
							<CircularProgress size={24} />
							<Typography variant="body1" sx={{ color: "#1976d2", fontWeight: 500 }}>
								Processing uploaded data... Please wait.
							</Typography>
						</Box>
					)}
				</Box>

				{/* Instructions Section */}
				<Box sx={{ p: 3, border: "1px solid #e0e0e0", borderRadius: 2, backgroundColor: "#f8f9fa", mb: 3 }}>
					<Typography variant="h6" sx={{ mb: 2, color: "#000", fontWeight: 600 }}>
						Instructions
					</Typography>
					<Stack spacing={1}>
						<Typography variant="body2" sx={{ color: "#666" }}>
							Date range is selected on the main page.
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							1. Upload a CSV with columns: <b>brand, department, style, color, pallete type</b>
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							2. Each row should match a product key: brand-department-style-color-pallete type
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							3. Click <b>"Apply to Filter"</b> to use the CSV data as filters on the main page. You will see a preview of filters (including additional options from the database), then click "Set Filters & Go to Main Page" to apply.
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 2, fontSize: "0.9em" }}>
							- The system fetches additional filter options (sub brand, sub category, sleeve, season, division,
							fashionability, occasion) from the database
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 2, fontSize: "0.9em" }}>
							- On the main page you can modify filters and click "Apply Filters" when ready
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							Example header:{" "}
							<span style={{ fontFamily: "monospace", color: "#2e7d32" }}>
								brand,department,style,color,pallete type
							</span>
						</Typography>
					</Stack>
				</Box>

			</Paper>
		</Container>
	);
};

export default UploadPage;
